import { DataView } from "@zebrabi/table-data";
import { mountChartChooser } from "@zebrabi/chart-chooser";
import {
    type CellValue,
    DataBindingIds,
    OfficeDataHelper,
    PivotFieldCounts,
    PivotTableInput,
    RangeInput,
    type WorksheetRow,
} from "@zebrabi/office-data";
import { looksLikeCommentHeader } from "@zebrabi/data-helpers/fieldAssignment";
import { isValidRangeInputData } from "@zebrabi/data-helpers/excelDataHelpers";
import { VISUAL_TYPE } from "./initialDefinitions";
import {
    dataViewToWorksheetRows,
    getMetadata,
    parseMultiMeasureDataView,
    parseMultiMeasureDataViewWithGroups,
    parseRangeDataView,
    parseRangeMultiplesDataView,
    parseSingleMeasureDataView,
    parseSingleMeasureDataViewWithGroups,
} from "./dataViewParsing";

export class DataHelper extends OfficeDataHelper<DataView> {
    protected readonly visualName = VISUAL_TYPE;
    protected readonly bindingIds: DataBindingIds = { pivotTable: "pivotTableDataChangeEvent", range: "chartsRangeChangeEvent" };
    protected readonly galleryName = "Charts gallery";

    protected mountChooser() {
        mountChartChooser();
    }

    protected dataViewToRows(dataView: DataView): WorksheetRow[] {
        return dataViewToWorksheetRows(dataView);
    }

    protected getEmptyDataView(msg: string): DataView {
        return {
            values: [],
            categories: [],
            rowFields: null,
            columnFields: null,
            valueFields: null,
            rowGrandTotal: null,
            columnGrandTotal: null,
            errorMessage: msg,
            metadata: null,
            rowNames: null,
            columnNames: null,
            valueNames: null,
        };
    }

    protected validatePivotFields({ rows, columns, values }: PivotFieldCounts): string | null {
        if (rows === 0 || values === 0) {
            return "Not enough fields. Please have at least one field in rows and one in values.";
        }
        if (rows > 1) {
            return "Too many fields. Please have exactly one field in rows.";
        }
        if (columns > 2) {
            // empty columns has one hidden column
            return "Too many fields. Please have no more than one field in columns.";
        }
        return null;
    }

    protected parsePivotTable(input: PivotTableInput): DataView {
        const { rowFields, columnFields, valueFields, rowLabelsValues, columnLabelsValues, dataValues } = input;

        let dataView: DataView = {
            values: [],
            categories: [],
            rowFields: rowFields,
            columnFields: columnFields,
            valueFields: valueFields,
            rowGrandTotal: input.rowGrandTotal, // adds grand total columns
            columnGrandTotal: input.columnGrandTotal, // adds grand total row
            rowNames: rowFields,
            columnNames: columnFields,
            valueNames: valueFields,
            metadata: { columns: [] },
        };

        dataView = getMetadata(dataView);

        if (dataView.valueFields.length < 1 || dataView.rowFields.length < 1 || dataView.rowFields.length > 1) {
            dataView.errorMessage = "Invalid data fields configuration or empty data";
            return dataView;
        }

        if (dataView.valueFields.length === 1) {
            if (dataView.columnFields.length === 0) {
                // 1 measure
                dataView = parseSingleMeasureDataView(dataView, rowLabelsValues, columnLabelsValues, dataValues);
            } else if (dataView.columnFields.length === 1) {
                // multiples 1
                dataView = parseSingleMeasureDataViewWithGroups(dataView, rowLabelsValues, columnLabelsValues, dataValues);
            }
        } else {
            if (dataView.columnFields.length === 1) {
                dataView = parseMultiMeasureDataViewWithGroups(dataView, rowLabelsValues, columnLabelsValues, dataValues);
            } else {
                dataView = parseMultiMeasureDataView(dataView, rowLabelsValues, columnLabelsValues, dataValues);
            }
        }

        // remove grand total row (column grand total on the Excel UI)
        if (!dataView.errorMessage && dataView.columnGrandTotal && dataView.categories.length) {
            dataView.categories[0].values.pop();
            dataView.values.forEach((v) => v.values.pop());
        }

        return dataView;
    }

    /** Charts read the first column as the single category column. */
    protected countCategoryColumns(): number {
        return 1;
    }

    protected parseRange({ range, headerRow, categoryLabelsValues }: RangeInput): DataView {
        const rowLabelsValues = categoryLabelsValues;
        const columnLabelsValues = headerRow.slice(1).map((cell: CellValue) => String(cell));

        const rowFields = [];
        let columnFields = [];
        const rowNames = [];
        let columnNames = [];

        let valueFields = columnLabelsValues;
        let measureCount = columnLabelsValues.length;

        if (columnLabelsValues.some((c: string) => looksLikeCommentHeader(c)) || this.hasCommentsDataCol(range.valueTypes)) {
            measureCount--;
        }
        let isMultiplesData = false;
        if (measureCount > 4) {
            // parse small multiples
            valueFields = ["Values"];
            columnNames = ["Group"];
            columnFields = ["Group"];
            isMultiplesData = true;
        }

        let dataView: DataView = {
            values: [],
            categories: [],
            rowFields: rowFields,
            columnFields: columnFields,
            valueFields: valueFields,
            rowGrandTotal: false,
            columnGrandTotal: false,
            rowNames: rowNames,
            columnNames: columnNames,
            valueNames: valueFields,
            metadata: { columns: [] },
        };

        if (!isValidRangeInputData(range)) {
            dataView.errorMessage = this.getDataParsingErrorMsgHtml();
            return dataView;
        }

        dataView = getMetadata(dataView);

        if (isMultiplesData) {
            dataView = parseRangeMultiplesDataView(dataView, rowLabelsValues, columnLabelsValues, range);
        } else {
            dataView = parseRangeDataView(dataView, rowLabelsValues, columnLabelsValues, range.values, range);
        }

        return dataView;
    }
}
